// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { RiskLevelOption } from '@api/types';
import { flattenBlocks, insertTenantRule, reorder, toBlocks } from './codec/order';
import type { ConditionOption, Decoded, EditorAction, EditorState, Rule } from './types';

export const conditionOptions = (
  entitled: boolean,
  riskLevels: RiskLevelOption[],
): ConditionOption[] =>
  entitled
    ? riskLevels.map(({ token, label }) => ({ condition: 'risk-level', conditionValue: token, label }))
    : [];

export const selectedRiskTokens = (rules: Rule[]): Set<string> =>
  new Set(
    rules.flatMap(rule =>
      rule.kind === 'tenant' && rule.condition === 'risk-level' && rule.conditionValue !== undefined
        ? [rule.conditionValue]
        : [],
    ),
  );

export const availableConditionOptions = (
  riskSelectable: boolean,
  riskLevels: RiskLevelOption[],
  rules: Rule[],
): ConditionOption[] => {
  const selected = selectedRiskTokens(rules);
  return conditionOptions(riskSelectable, riskLevels).filter(
    option => !selected.has(option.conditionValue),
  );
};

export const canAddCondition = (
  riskSelectable: boolean,
  riskLevels: RiskLevelOption[],
  rules: Rule[],
): boolean => availableConditionOptions(riskSelectable, riskLevels, rules).length > 0;

export const seedEditorState = (decoded: Decoded): EditorState => ({ ...decoded, nextKey: 1 });

// Returns `state` by reference when nothing changed, which is how `useReducer` skips a re-render.
const patchRule = (state: EditorState, key: string, patch: (rule: Rule) => Rule): EditorState => {
  const target = state.rules.find(rule => rule.key === key);
  const next = target && patch(target);
  if (next === undefined || next === target) { return state; }
  return { ...state, rules: state.rules.map(rule => (rule === target ? next : rule)) };
};

/** The editor's one command surface. Returns state and nothing else (CQS). */
export const editorReducer = (state: EditorState, action: EditorAction): EditorState => {
  switch (action.type) {
    case 'setAction':
      return patchRule(state, action.key, rule => ({ ...rule, action: action.action }));

    case 'setCondition': {
      const takenElsewhere = selectedRiskTokens(state.rules.filter(rule => rule.key !== action.key));
      if (action.conditionValue !== undefined && takenElsewhere.has(action.conditionValue)) {
        return state;
      }
      return patchRule(state, action.key, rule =>
        rule.kind === 'tenant'
          ? { ...rule, condition: action.condition, conditionValue: action.conditionValue }
          : rule,
      );
    }

    case 'addTenantRule': {
      const first = availableConditionOptions(action.entitled, action.riskLevels, state.rules).at(0);
      if (first === undefined) { return state; }

      const allowedValues = [...new Set(state.rules.flatMap(rule => rule.allowedValues))];
      const added: Rule = {
        kind: 'tenant',
        key: `n${state.nextKey}`,
        condition: first.condition,
        conditionValue: first.conditionValue,
        action: allowedValues.at(0),
        caps: { conditionLocked: false, valueEditable: true, enabled: true },
        allowedValues,
        served: null,
      };
      return { ...state, rules: insertTenantRule(state.rules, added), nextKey: state.nextKey + 1 };
    }

    case 'removeTenantRule': {
      const rules = state.rules.filter(rule => rule.kind !== 'tenant' || rule.key !== action.key);
      return rules.length === state.rules.length ? state : { ...state, rules };
    }

    // Addressed by block so a move cannot cross one: cross-block order is the backend's (REQ-016).
    case 'moveWithinBlock': {
      const blocks = toBlocks(state.rules);
      const block = blocks[action.blockIndex];
      if (block?.kind !== 'tenantRuleBlock') { return state; }

      const moved = reorder(block.rules, action.from, action.to);
      if (moved === null) { return state; }

      return {
        ...state,
        rules: flattenBlocks(blocks.map(item => (item === block ? { ...block, rules: moved } : item))),
      };
    }
  }
};
